import { ScrollArea } from "@/components/ui/scroll-area";

export function TermsOfService() {
  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
      <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase mb-6" data-testid="terms-title">TERMS OF SERVICE</h1>
      <ScrollArea className="h-[70vh]">
        <div className="prose prose-invert text-sm text-[#a3a3a3] space-y-4">
          <p className="text-[10px] text-[#666]">Last updated: April 2026</p>
          
          <h3 className="text-white font-heading text-lg uppercase">1. Acceptance of Terms</h3>
          <p>By accessing or using Dynasty Trade Maker ("the Service"), operated at dynastytrademaker.com, you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">2. Description of Service</h3>
          <p>Dynasty Trade Maker is a dynasty fantasy football trade analysis tool that integrates with the Sleeper platform and FantasyCalc to provide player valuations, trade suggestions, and roster analysis. The Service uses artificial intelligence to generate trade recommendations.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">3. Accounts and Authentication</h3>
          <p>Free users may access limited features by entering their Sleeper username. Pro users must authenticate via Google OAuth. You are responsible for maintaining the confidentiality of your account. You must be at least 18 years old to create an account.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">4. Subscription and Billing</h3>
          <p>Pro subscriptions are billed monthly ($9.99/month) or annually ($79.99/year) via Stripe. Subscriptions auto-renew unless cancelled. Cancellations take effect at the end of the current billing period — you retain Pro access until then. No refunds for partial billing periods.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">5. Third-Party Services</h3>
          <p>The Service integrates with third-party platforms including:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Sleeper</strong> — We access your public league data via the Sleeper API. We do not store your Sleeper password.</li>
            <li><strong>FantasyCalc</strong> — Player dynasty values are sourced from FantasyCalc's public API.</li>
            <li><strong>Google</strong> — Authentication is handled via Google OAuth.</li>
            <li><strong>OpenAI</strong> — AI trade recommendations are powered by OpenAI's GPT models.</li>
            <li><strong>Stripe</strong> — Payment processing is handled by Stripe.</li>
          </ul>
          <p>We are not responsible for the availability or accuracy of third-party services.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">6. AI-Generated Content</h3>
          <p>Trade suggestions and roster analysis are generated by artificial intelligence. These are recommendations only — not financial or professional advice. AI outputs may contain errors. You are solely responsible for any trades you execute based on our suggestions.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">7. Limitation of Liability</h3>
          <p>The Service is provided "as is" without warranties of any kind. We are not liable for any losses resulting from use of the Service, including but not limited to: poor trade outcomes, inaccurate player valuations, service downtime, or data loss.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">8. Intellectual Property</h3>
          <p>All content, features, and functionality of the Service are owned by Dynasty Trade Maker. Player data is sourced from third-party APIs and remains the property of their respective owners.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">9. Termination</h3>
          <p>We reserve the right to suspend or terminate accounts that violate these terms, abuse the Service, or engage in unauthorized scraping or redistribution of data.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">10. Changes to Terms</h3>
          <p>We may update these terms at any time. Continued use of the Service after changes constitutes acceptance of the new terms.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">11. Contact</h3>
          <p>For questions about these terms, contact us at derek@emerald.consulting.</p>
        </div>
      </ScrollArea>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
      <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase mb-6" data-testid="privacy-title">PRIVACY POLICY</h1>
      <ScrollArea className="h-[70vh]">
        <div className="prose prose-invert text-sm text-[#a3a3a3] space-y-4">
          <p className="text-[10px] text-[#666]">Last updated: April 2026</p>
          
          <h3 className="text-white font-heading text-lg uppercase">1. Information We Collect</h3>
          <p><strong>Account Information:</strong> When you sign in with Google, we collect your name, email address, and profile picture. When you link your Sleeper account, we store your Sleeper username and user ID.</p>
          <p><strong>League Data:</strong> We access your Sleeper league rosters, draft picks, and league settings via the public Sleeper API. This data is used to provide trade analysis and is cached temporarily for performance.</p>
          <p><strong>Usage Data:</strong> We track feature usage (which tools you use and how often) to improve the Service. We do not track individual trade decisions.</p>
          <p><strong>Payment Data:</strong> Payment processing is handled entirely by Stripe. We store transaction IDs and subscription status but never see or store your credit card information.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">2. How We Use Your Information</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>To provide trade analysis and roster management features</li>
            <li>To manage your account and subscription</li>
            <li>To send transactional emails (welcome, subscription, cancellation)</li>
            <li>To improve the Service based on usage patterns</li>
            <li>To provide customer support</li>
          </ul>
          
          <h3 className="text-white font-heading text-lg uppercase">3. Data Sharing</h3>
          <p>We do not sell your personal information. We share data only with:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Stripe</strong> — for payment processing</li>
            <li><strong>SendGrid</strong> — for transactional emails</li>
            <li><strong>OpenAI</strong> — roster data is sent to generate AI recommendations (no personal info)</li>
            <li><strong>Google</strong> — for authentication</li>
          </ul>
          
          <h3 className="text-white font-heading text-lg uppercase">4. Cookies</h3>
          <p>We use a single httpOnly session cookie ("session_token") for authentication. We do not use advertising or tracking cookies. We store your preferences in browser localStorage.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">5. Data Retention</h3>
          <p>Account data is retained as long as your account is active. League data is cached for up to 1 hour for performance. You can request deletion of your account and data by contacting us.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">6. Data Security</h3>
          <p>We use industry-standard security measures including HTTPS encryption, secure httpOnly cookies, and server-side session management. Authentication is handled via Google OAuth — we never store passwords.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">7. Your Rights</h3>
          <p>You have the right to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Access your personal data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and data</li>
            <li>Cancel your subscription at any time</li>
          </ul>
          
          <h3 className="text-white font-heading text-lg uppercase">8. Children's Privacy</h3>
          <p>The Service is not intended for users under 18. We do not knowingly collect data from minors.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">9. Changes to This Policy</h3>
          <p>We may update this policy periodically. We will notify users of significant changes via email.</p>
          
          <h3 className="text-white font-heading text-lg uppercase">10. Contact</h3>
          <p>For privacy-related questions or data requests, contact derek@emerald.consulting.</p>
        </div>
      </ScrollArea>
    </div>
  );
}
